import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { Button } from '@components/ui/button';

interface ConfirmationStepProps {
  bookingId: string;
  trackingToken: string;
  /** ISO timestamp. Null when reservation already `confirmed`. */
  confirmationDeadline: string | null;
  onReset: () => void;
}

export function ConfirmationStep({
  trackingToken,
  confirmationDeadline,
  onReset,
}: ConfirmationStepProps) {
  const { t, i18n } = useTranslation();

  const deadlineHint = formatDeadlineHint(t, i18n.language, confirmationDeadline);

  return (
    <section
      aria-label={t('booking.confirmationTitle')}
      className="flex flex-col items-center gap-6 py-8 text-center"
      data-testid="confirmation-step"
    >
      <CheckCircle className="h-16 w-16 text-green-500" aria-hidden="true" />
      <div>
        <h2 className="text-xl font-bold">{t('booking.confirmationTitle')}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t('booking.confirmationMessage')}</p>
        {deadlineHint && (
          <p
            className="mt-3 text-sm font-medium text-amber-700"
            data-testid="confirmation-deadline-hint"
          >
            {deadlineHint}
          </p>
        )}
      </div>

      {trackingToken && (
        <a
          href={`/c/${trackingToken}`}
          className="text-sm text-primary underline underline-offset-2"
          data-testid="confirmation-tracking-link"
        >
          Ver mi reserva
        </a>
      )}

      <Button variant="outline" onClick={onReset} data-testid="confirmation-reset">
        Nueva reserva
      </Button>
    </section>
  );
}

function formatDeadlineHint(
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: string,
  deadlineIso: string | null,
): string | null {
  if (!deadlineIso) return null;
  const date = new Date(deadlineIso);
  if (Number.isNaN(date.getTime())) return null;
  const formatted = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return t('booking.confirmationDeadlineHint', { deadline: formatted });
}
